import React, { forwardRef, memo, useImperativeHandle } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import { useSKU } from 'hooks/useSKU';

import { useAppSelector } from 'store/hooks';
import { locationSelector, washhubSelector } from 'store/slices/washhub';

import { createItem } from 'api/items';
import { useMutation, useQueryClient } from 'react-query';

import { successToast } from 'utils/successToast';

import { RefType, AddItemModalProps } from './AddItemModal.props';

import AddItemModalView from './AddItemModal.view';

const Container = forwardRef<RefType, AddItemModalProps>((_, ref) => {
  const queryClient = useQueryClient();
  const washHubs = useAppSelector(washhubSelector);
  const selectedWashHub = useAppSelector(locationSelector);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: skus = [], isLoading: isLoadingSKU } = useSKU(
    'item',
    selectedWashHub?._id,
  );

  const { mutate, isLoading } = useMutation(createItem, {
    onSuccess: () => {
      successToast('Item created successfully');

      queryClient.invalidateQueries(`fetch-items-${selectedWashHub?._id}`);

      queryClient.invalidateQueries(
        `fetch-items-status-count-${selectedWashHub?._id}`,
        {
          exact: true,
        },
      );

      onClose();
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      onOpen,
    }),
    [],
  );

  const onSubmit = (values: any) => {
    const skuCode = skus?.find((s) => s._id === values?.sku)?.code;
    const washHubCode = washHubs?.find((w) => w._id === selectedWashHub?._id)
      ?.code;

    values = {
      washHub: selectedWashHub?._id,
      washHubCode,
      items: values?.items?.map((v: any) => ({
        rfid: v,
        skuCode,
        sku: values?.sku,
      })),
    };

    mutate(values);
  };

  const _props = {
    skus,
    isOpen,
    onClose,
    onSubmit,
    washHubs,
    isLoading,
    isLoadingSKU,
  };

  return <AddItemModalView {..._props} />;
});

const AddItemModal = memo(Container);

export default AddItemModal;
